import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, MessageSquare, MessageCircle, Facebook, Instagram, Linkedin, FileText, Download, Twitter, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { companyInfo } from '../mock';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

// PDF Icon Component (Adobe Acrobat style)
const PdfIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20ZM9 13.5C9 14.33 8.33 15 7.5 15H7V16.5H5.5V12H7.5C8.33 12 9 12.67 9 13.5ZM7 13.5V14H7.5C7.78 14 8 13.78 8 13.5S7.78 13 7.5 13H7V13.5ZM14.5 12C15.33 12 16 12.67 16 13.5V15C16 15.83 15.33 16.5 14.5 16.5H12.5V12H14.5ZM14 15H14.5C14.78 15 15 14.78 15 14.5V13.5C15 13.22 14.78 13 14.5 13H14V15ZM18.5 12V13.5H17.5V14H18.5V15.5H17.5V16.5H16V12H18.5Z" />
  </svg>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Social media links
const socialLinks = [
  { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/guritamandalapersada', color: 'bg-[#1877F2] hover:bg-[#155db2]' },
  { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/guritamandalapersada/', color: 'bg-[#E4405F] hover:bg-[#d12e4a]' },
  { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/in/guritamandalapersada/', color: 'bg-[#0A66C2] hover:bg-[#08508f]' },
  { name: 'X (Twitter)', icon: Twitter, url: 'https://x.com/@GuritaMandala', color: 'bg-black hover:bg-gray-800' },
  { name: 'Email', icon: Mail, url: 'mailto:info@guritamandala.co.id', color: 'bg-[#CF2E2E] hover:bg-[#B02525]' }
];

const Contact = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    industry: '',
    productInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const requestData = {
        company_name: formData.companyName,
        contact_person: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        industry: formData.industry || null,
        product_interest: formData.productInterest || null,
        message: formData.message
      };

      const response = await axios.post(`${API}/quotations`, requestData);

      if (response.data.success) {
        toast.success(t('contact.successTitle'), {
          description: response.data.message
        });
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          industry: '',
          productInterest: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting quotation:', error);
      const errorMessage = error.response?.data?.detail || t('contact.errorTitle');
      toast.error(t('contact.errorTitle'), {
        description: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = (number) => {
    const message = encodeURIComponent(
      'Halo, saya ingin bertanya tentang produk industri Anda.'
    );

    const cleanNumber = number.replace(/\D/g, '');

    window.open(`https://wa.me/${cleanNumber}?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-20">
      <Toaster position="top-right" />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('contact.pageTitle')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('contact.pageSubtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#0693E3]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.visitUs')}</h3>
                    <p className="text-gray-600 text-sm">{companyInfo.address}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#CF2E2E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.callUs')}</h3>
                    <p className="text-gray-600 text-sm mb-1">{companyInfo.phone}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="text-green-600" size={24} />
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-semibold text-gray-900 mb-2">
                      {t('contact.whatsappUs')}
                    </h3>

                    <button
                      onClick={() => handleWhatsApp(companyInfo.whatsapp1)}
                      className="block text-sm text-green-600 hover:underline"
                    >
                      {companyInfo.whatsapp1}
                    </button>

                    <button
                      onClick={() => handleWhatsApp(companyInfo.whatsapp2)}
                      className="block text-sm text-green-600 hover:underline"
                    >
                      {companyInfo.whatsapp2}
                    </button>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#0693E3]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.emailUs')}</h3>
                    <p className="text-gray-600 text-sm">{companyInfo.email}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#CF2E2E]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.businessHours')}</h3>
                    <p className="text-gray-600 text-sm">{t('contact.businessHoursWeekday')}</p>
                    <p className="text-gray-600 text-sm">{t('contact.businessHoursSaturday')}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-blue-50 border-blue-200">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#0693E3] rounded-full flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">{t('contact.quickResponse')}</h3>
                    <p className="text-gray-700 text-sm">
                      {t('contact.quickResponseDesc')}
                    </p>
                  </div>
                </div>
              </Card>

              {/* Social Media Links */}
              <Card className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">
                  {language === 'id' ? 'Ikuti Kami' : 'Follow Us'}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {socialLinks.map((social) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center space-x-2 px-4 py-2 ${social.color} text-white rounded-lg transition-all hover:scale-105 shadow-sm`}
                        title={social.name}
                      >
                        <Icon size={18} />
                        <span className="text-sm font-medium">{social.name}</span>
                      </a>
                    );
                  })}
                </div>
              </Card>

              {/* Download Company Profile */}
              <Card className="p-6 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                <h3 className="font-semibold text-gray-900 mb-3">
                  {language === 'id' ? 'Company Profile' : 'Company Profile'}
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  {language === 'id'
                    ? 'Unduh company profile kami untuk informasi lengkap tentang produk dan layanan kami.'
                    : 'Download our company profile for complete information about our products and services.'}
                </p>
                <a
                  href="/company-profile.pdf"
                  download="PT-Gurita-Mandala-Persada-Company-Profile.pdf"
                  className="flex items-center justify-center space-x-3 w-full px-6 py-4 bg-[#CF2E2E] hover:bg-[#B02525] text-white rounded-lg transition-all hover:scale-[1.02] shadow-lg hover:shadow-xl font-semibold"
                >
                  <PdfIcon />
                  <span>{language === 'id' ? 'Unduh Company Profile' : 'Download Company Profile'}</span>
                  <Download size={18} />
                </a>
              </Card>
            </div>

            {/* Quotation Form */}
            <div className="lg:col-span-2">
              <Card className="p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('contact.formTitle')}</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.companyName')} *
                      </label>
                      <Input
                        type="text"
                        name="companyName"
                        required
                        value={formData.companyName}
                        onChange={handleChange}
                        placeholder={t('contact.placeholders.companyName')}
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.contactPerson')} *
                      </label>
                      <Input
                        type="text"
                        name="contactPerson"
                        required
                        value={formData.contactPerson}
                        onChange={handleChange}
                        placeholder={t('contact.placeholders.contactPerson')}
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.emailAddress')} *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.placeholders.email')}
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.phoneNumber')} *
                      </label>
                      <Input
                        type="tel"
                        name="phone"
                        required
                        minLength={10}
                        maxLength={20}
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t('contact.placeholders.phone')}
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.industry')}
                      </label>
                      <Input
                        type="text"
                        name="industry"
                        value={formData.industry}
                        onChange={handleChange}
                        placeholder={t('contact.placeholders.industry')}
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('contact.productInterest')}
                      </label>
                      <Input
                        type="text"
                        name="productInterest"
                        value={formData.productInterest}
                        onChange={handleChange}
                        placeholder={t('contact.placeholders.productInterest')}
                        className="h-12"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.message')} *
                    </label>
                    <Textarea
                      name="message"
                      required
                      minLength={10}
                      maxLength={2000}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.placeholders.message')}
                      rows={6}
                      className="resize-none"
                      disabled={isSubmitting}
                    />
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-[#CF2E2E] hover:bg-[#B02525] text-white text-lg py-6 flex flex-row items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        {t('contact.submitting')}
                      </>
                    ) : (
                      t('contact.submitButton')
                    )}
                  </Button>

                  <p className="text-sm text-gray-500 text-center">
                    {t('contact.requiredFields')}
                  </p>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {language === 'id' ? 'Lokasi Kami' : 'Our Location'}
            </h2>
            <p className="text-gray-600">{companyInfo.address}</p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3599.6313469150195!2d107.59124949999999!3d-6.9352371999999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e68e8a40d6a506f%3A0xd37dddb33c095dcb!2sPT.%20Gurita%20Mandala%20Persada!5e1!3m2!1sen!2sid!4v1773644172002!5m2!1sen!2sid"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="PT. Gurita Mandala Persada Location"
              className="w-full"
            />
          </div>

          {/* Directions Button */}
          <div className="text-center mt-6">
            <a
              href="https://maps.app.goo.gl/n32EJXrcHHy8ukyNA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-[#0693E3] hover:bg-[#0578BE] text-white rounded-lg transition-all hover:scale-105 shadow-lg"
            >
              <MapPin size={20} />
              <span className="font-semibold">
                {language === 'id' ? 'Buka di Google Maps' : 'Open in Google Maps'}
              </span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;